import { useState, useEffect } from 'react';
import { supabase } from '@/utils/supabase';
import { createComponentLogger } from '@/utils/logger';

const log = createComponentLogger('useAvailableFolders', 'encompass-prospects');

export interface FolderCount {
  folder: string;
  count: number;
}

export function useAvailableFolders(organizationId: string | undefined) {
  const [folders, setFolders] = useState<FolderCount[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!organizationId) {
      setFolders([]);
      setIsLoading(false);
      return;
    }

    const fetchFolders = async () => {
      setIsLoading(true);
      try {
        const { data, error } = await supabase
          .from('encompass_loans')
          .select('loan_folder')
          .eq('organization_id', organizationId)
          .eq('deleted_from_encompass', false)
          .neq('loan_folder', 'Transferred Data');

        if (error) {
          log.error('Failed to fetch folders', { organizationId }, error);
          return;
        }

        // Count by folder
        const folderCounts = new Map<string, number>();
        for (const row of data || []) {
          const folder = row.loan_folder || 'Unknown';
          folderCounts.set(folder, (folderCounts.get(folder) || 0) + 1);
        }

        // Convert to array and sort by count
        const result: FolderCount[] = Array.from(folderCounts.entries())
          .map(([folder, count]) => ({ folder, count }))
          .sort((a, b) => b.count - a.count);

        setFolders(result);
      } catch (err) {
        log.error('Error fetching folders', { organizationId }, err as Error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchFolders();
  }, [organizationId]);

  return { folders, isLoading };
}
